'use client';

import { SwiperSlide } from 'swiper/react';
import type { HeroSlideViewModel } from '@/lib/home-view-model';
import { sanitizeHeroDesign, type HeroDesign } from '@/lib/theme-settings';
import { HeroCarouselShell } from '@/themes/legacy/hero/HeroCarouselShell';
import { heroSlideStyles } from '@/themes/legacy/hero/heroSlideStyles';
import './theme01-hero.css';

type Theme01HeroProps = {
  slides: HeroSlideViewModel[];
  design: HeroDesign;
};

/** Marble.Theme.01 hero: the boxed carousel — background image behind left-aligned text, framed by the content column. */
export function Theme01Hero({ slides, design: designProp }: Theme01HeroProps) {
  const design = sanitizeHeroDesign(designProp);

  return (
    <HeroCarouselShell variantClassName="bg-gray-1 rounded-lg shadow-2" design={design}>
      {slides.map((slide) => (
        <SwiperSlide key={slide.id}>
          <Theme01HeroSlide slide={slide} design={design} />
        </SwiperSlide>
      ))}
    </HeroCarouselShell>
  );
}

function Theme01HeroSlide({ slide, design }: { slide: HeroSlideViewModel; design: HeroDesign }) {
  const { heightClass, titleStyle, descriptionStyle, subtitleStyle, ctaClass, ctaStyle } = heroSlideStyles(design);

  return (
    <div
      className={`relative w-full h-auto overflow-hidden min-h-[300px] sm:min-h-[320px] md:min-h-[340px] lg:min-h-[360px]${heightClass}`}
    >
      {/* Background Image — desktop/tablet layer; identical to the original
          single-div markup, just gated to md+ so the mobile layer below can
          take over beneath that breakpoint. */}
      <div
        className="absolute inset-0 bg-cover bg-center hidden md:block"
        style={{
          backgroundImage: `url(${slide.image})`,
        }}
      />
      {/* Mobile layer: dedicated mobile image when set, otherwise the same
          desktop image — so with no mobileImage this renders pixel-identical
          to the pre-existing single-div behavior. */}
      <div
        className="absolute inset-0 bg-cover bg-center md:hidden"
        style={{
          backgroundImage: `url(${slide.mobileImage || slide.image})`,
        }}
      />

      {/* Content */}
      {/* Below md the boxed hero's arrows sit in the bottom corners (see theme01-hero.css), so the bottom 76px
          (20px offset + 48px arrow + 8px) is reserved: text can never run under them. md and up is unchanged. */}
      <div className="relative h-full flex flex-col items-start justify-center px-4 text-left pt-8 pb-[76px] sm:pt-10 md:pb-10">
        {/* Subtitle */}
        {slide.subtitle && (
          <p className="text-primary font-semibold text-sm md:text-base mb-2 md:mb-3 animate-fade-in tracking-wider uppercase" style={subtitleStyle}>
            {slide.subtitle}
          </p>
        )}

        {/* Title */}
        <h1 className="text-3xl md:text-4xl text-dark mb-3 md:mb-4 max-w-4xl leading-tight animate-fade-in" style={titleStyle('100ms')}>
          {slide.title}
        </h1>

        {/* Description */}
        {slide.description && (
          <p className="text-dark-3 text-base md:text-lg max-w-2xl mb-6 md:mb-8 animate-fade-in" style={descriptionStyle('200ms')}>
            {slide.description}
          </p>
        )}

        {/* CTA Button */}
        {slide.cta && (
          <a
            href={slide.cta.link}
            className={`inline-block px-8 py-3 md:px-10 md:py-4 bg-primary text-white font-semibold text-base rounded-lg hover:bg-primary-dark transition-all duration-200 shadow-2 hover:shadow-3 hover:-translate-y-1 animate-fade-in${ctaClass}`}
            style={ctaStyle('300ms')}
          >
            {slide.cta.text}
            <span className="ml-2">→</span>
          </a>
        )}
      </div>

      {/* Bottom Accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-light to-primary-light-3" />
    </div>
  );
}
